import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getReminders,
  createReminder,
  deleteReminder,
  retryReminder,
} from '../services/remindersApi'
import type { DocumentReminder, CreateReminderRequest } from '../services/remindersApi'

/** List reminders for a document */
export function useReminders(koblinkId: string | undefined, actorId?: string) {
  return useQuery<DocumentReminder[]>({
    queryKey: ['reminders', koblinkId, actorId],
    queryFn: () => getReminders(koblinkId!, actorId),
    enabled: !!koblinkId,
    staleTime: 60_000,
  })
}

/** Create a reminder for a document */
export function useCreateReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ koblinkId, data, actorId }: { koblinkId: string; data: CreateReminderRequest; actorId?: string }) =>
      createReminder(koblinkId, data, actorId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reminders', variables.koblinkId] })
    },
  })
}

/** Delete a reminder */
export function useDeleteReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reminderId: number) => deleteReminder(reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
    },
  })
}

/** Retry a failed reminder */
export function useRetryReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reminderId: number) => retryReminder(reminderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
    },
  })
}
